// ============================================================
// App route table — single source of truth for routing AND the
// AdminPopup 顯示頁面 selection list (derived automatically).
// 新增顧客頁面時加上 label，/admin/popup 的頁面篩選清單即自動出現；
// admin / 無 label 的路由不會列入。
// ============================================================

import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

// Customer pages
import Home from '../pages/Home';
import Story from '../pages/Story';
import ChiliFans from '../pages/ChiliFans';
import Chiliclub from '../pages/Chiliclub';
import MediaCoverage from '../pages/MediaCoverage';
import NewsDetail from '../pages/NewsDetail';
import ProductDetail from '../pages/ProductDetail';
import Store from '../pages/Store';
import Offers from '../pages/Offers';
import Redeem from '../pages/Redeem';
import Godfather from '../pages/Godfather';
import AdminGodfather from '../pages/AdminGodfather';
import AdminWanla from '../pages/AdminWanla';
import AdminSyslog from '../pages/AdminSyslog';
import AdminMore from '../pages/AdminMore';
import AdminCsTemplates from '../pages/AdminCsTemplates';
import AdminPatterns from '../pages/AdminPatterns';
import GodfatherPickup from '../pages/GodfatherPickup';
import AdminSystem from '../pages/AdminSystem';
import AdminCampaigns from '../pages/AdminCampaigns';
import MerchantSubmitOffer from '../pages/MerchantSubmitOffer';
import MerchantAgreement from '../pages/MerchantAgreement';
import MyCenter from '../pages/MyCenter';
import SupplierSubmitProduct from '../pages/SupplierSubmitProduct';
import MerchantCenter from '../pages/MerchantCenter';
import Cart from '../pages/Cart';
import Checkout from '../pages/Checkout';
import CheckoutConfirm from '../pages/CheckoutConfirm';
import OrderSuccess from '../pages/OrderSuccess';
import OrderQuery from '../pages/OrderQuery';
import PaymentProof from '../pages/PaymentProof';
import ReferralDashboard from '../pages/ReferralDashboard';
import Terms from '../pages/Terms';
import Privacy from '../pages/Privacy';
import MoneyPrinting from '../pages/MoneyPrinting';

// Existing pages (kept for backward compatibility)
import OrderConfirmation from '../pages/OrderConfirmation';
import PaymentProofUpload from '../pages/PaymentProofUpload';

// Admin layout & pages (GoodStore2 full admin suite)
import AdminLogin from '../pages/AdminLogin';
import AdminDashboard from '../pages/AdminDashboard';
import AdminOrders from '../pages/AdminOrders';
import AdminOrderDetail from '../pages/AdminOrderDetail';
import AdminProducts from '../pages/AdminProducts';
import AdminSupplier from '../pages/AdminSupplier';
import AdminMerchants from '../pages/AdminMerchants';
import AdminPackageConfigs from '../pages/AdminPackageConfigs';
import AdminCustomers from '../pages/AdminCustomers';
import AdminUsers from '../pages/AdminUsers';
import AdminReferrals from '../pages/AdminReferrals';
import AdminReferralDetail from '../pages/AdminReferralDetail';
import AdminReferralTree from '../pages/AdminReferralTree';
import AdminPromoCodes from '../pages/AdminPromoCodes';
import AdminMediaLibrary from '../pages/AdminMediaLibrary';
import AdminBroadcast from '../pages/AdminBroadcast';
import AdminBroadcastDetail from '../pages/AdminBroadcastDetail';
import AdminConversations from '../pages/AdminConversations';
import AdminSettings from '../pages/AdminSettings';
import AdminAuditLogs from '../pages/AdminAuditLogs';
import AdminCMS from '../pages/AdminCMS';
import AdminMediaNews from '../pages/AdminMediaNews';
import AdminWaBind from '../pages/AdminWaBind';
import AdminAnalytics from '../pages/AdminAnalytics';
import AdminAhJimContent from '../pages/AdminAhJimContent';
import AdminAutoTags from '../pages/AdminAutoTags';
import AdminCustomerJourneys from '../pages/AdminCustomerJourneys';
import AdminGiftCatalog from '../pages/AdminGiftCatalog';
import AdminPendingGifts from '../pages/AdminPendingGifts';
import AdminPendingJobs from '../pages/AdminPendingJobs';
import AdminOffers from '../pages/AdminOffers';
import AdminPopup from '../pages/AdminPopup';
import AdminSitemap from '../pages/AdminSitemap';
import AdminJourneys from '../pages/AdminJourneys';
import AdminInsights from '../pages/AdminInsights';

export interface AppRoute {
  path: string;
  element: ReactElement;
  /** 顧客頁顯示名稱；有 label 的路由會自動出現在彈窗後台的顯示頁面清單 */
  label?: string;
}

export const APP_ROUTES: AppRoute[] = [
  // Customer routes（label = 彈窗後台顯示名稱）
  { path: '/', element: <Home />, label: '首頁' },
  { path: '/story', element: <Story />, label: '香港製造' },
  { path: '/media-coverage', element: <MediaCoverage />, label: '媒體報導' },
  { path: '/media-coverage/:id', element: <NewsDetail />, label: '報導詳情' },
  { path: '/products', element: <Navigate to="/store" replace /> },
  { path: '/products/:slug', element: <ProductDetail />, label: '產品詳情' },
  { path: '/p/:id', element: <ProductDetail />, label: '產品短連結' },
  { path: '/store', element: <Store />, label: '商店' },
  { path: '/offers', element: <Offers />, label: '食肆優惠' },
  { path: '/offers/:key', element: <Offers /> },
  { path: '/c/:key', element: <Offers /> },
  { path: '/redeem/:code', element: <Redeem />, label: '優惠兌換頁' },
  { path: '/godfather', element: <Godfather />, label: 'FB 群友禮遇' },
  { path: '/godfather/:key', element: <Godfather />, label: '推廣活動頁' },
  { path: '/admin/godfather', element: <AdminGodfather /> },
  { path: '/admin/wanla', element: <AdminWanla /> },
  { path: '/admin/syslog', element: <AdminSyslog /> },
  { path: '/admin/more', element: <AdminMore /> },
  { path: '/admin/cs-templates', element: <AdminCsTemplates /> },
  { path: '/admin/patterns', element: <AdminPatterns /> },
  { path: '/admin/system', element: <AdminSystem /> },
  { path: '/admin/campaigns', element: <AdminCampaigns /> },
  { path: '/r/:code', element: <Redeem /> },
  { path: '/gf-pickup/:code', element: <GodfatherPickup /> },
  { path: '/me/:code', element: <MyCenter />, label: '客戶優惠中心' },
  { path: '/merchant/submit-offer', element: <MerchantSubmitOffer />, label: '商家提交優惠' },
  { path: '/merchant/agreement', element: <MerchantAgreement />, label: '商家服務協議' },
  { path: '/supplier/submit-product', element: <SupplierSubmitProduct />, label: '供貨商提交產品' },
  { path: '/supplier/portal', element: <SupplierSubmitProduct />, label: '供貨商門戶' },
  { path: '/merchant/center', element: <MerchantCenter />, label: '商家中心' },
  { path: '/cart', element: <Cart />, label: '購物車' },
  { path: '/checkout', element: <Checkout />, label: '結帳' },
  { path: '/checkout/confirm/:orderNum', element: <CheckoutConfirm /> },
  { path: '/order/success', element: <OrderSuccess /> },
  { path: '/order/:orderNum', element: <OrderQuery />, label: '訂單頁' },
  { path: '/o/:orderNum', element: <OrderSuccess />, label: '訂單短連結' },
  { path: '/payment-proof/:orderNum', element: <PaymentProof />, label: '付款憑證' },
  { path: '/ref/d/:token', element: <ReferralDashboard />, label: '推薦看板' },
  { path: '/terms', element: <Terms />, label: '條款' },
  { path: '/privacy', element: <Privacy />, label: '隱私' },
  { path: '/moneyprinting', element: <MoneyPrinting />, label: 'MoneyPrinting' },
  // 內部策略簡報（辣友會行動計劃）——無 label：不入 popup 清單；robots.txt 已 Disallow
  { path: '/chilifans', element: <ChiliFans /> },
  // 辣友會落地頁——未上線（chiliclub_live=0 閘）；無 label、robots Disallow
  { path: '/chiliclub', element: <Chiliclub /> },
  // Legacy / compatibility routes
  { path: '/order/success-legacy', element: <OrderConfirmation /> },
  { path: '/payment-proof-upload/:orderNum', element: <PaymentProofUpload /> },
  // Admin routes（彈窗永遠排除 /admin）
  { path: '/admin', element: <AdminLogin /> },
  { path: '/admin/dashboard', element: <AdminDashboard /> },
  { path: '/admin/orders', element: <AdminOrders /> },
  { path: '/admin/orders/:id', element: <AdminOrderDetail /> },
  { path: '/admin/products', element: <AdminProducts /> },
  { path: '/admin/supplier', element: <AdminSupplier /> },
  { path: '/admin/merchants', element: <AdminMerchants /> },
  { path: '/admin/package-configs', element: <AdminPackageConfigs /> },
  { path: '/admin/customers', element: <AdminCustomers /> },
  { path: '/admin/referrals', element: <AdminReferrals /> },
  { path: '/admin/referrals/:phone', element: <AdminReferralDetail /> },
  { path: '/admin/referrals/:phone/tree', element: <AdminReferralTree /> },
  { path: '/admin/promo-codes', element: <AdminPromoCodes /> },
  { path: '/admin/users', element: <AdminUsers /> },
  { path: '/admin/media-library', element: <AdminMediaLibrary /> },
  { path: '/admin/broadcast', element: <AdminBroadcast /> },
  { path: '/admin/broadcast/:id', element: <AdminBroadcastDetail /> },
  { path: '/admin/conversations', element: <AdminConversations /> },
  { path: '/admin/settings', element: <AdminSettings /> },
  { path: '/admin/audit-logs', element: <AdminAuditLogs /> },
  { path: '/admin/cms', element: <AdminCMS /> },
  { path: '/admin/media-news', element: <AdminMediaNews /> },
  { path: '/admin/wa-bind', element: <AdminWaBind /> },
  { path: '/admin/analytics', element: <AdminAnalytics /> },
  { path: '/admin/journeys', element: <AdminJourneys /> },
  { path: '/admin/insights', element: <AdminInsights /> },
  { path: '/admin/default-responses', element: <AdminAhJimContent /> },
  { path: '/admin/auto-tags', element: <AdminAutoTags /> },
  { path: '/admin/customer-journeys', element: <AdminCustomerJourneys /> },
  { path: '/admin/gift-catalog', element: <AdminGiftCatalog /> },
  { path: '/admin/pending-gifts', element: <AdminPendingGifts /> },
  { path: '/admin/pending-jobs', element: <AdminPendingJobs /> },
  { path: '/admin/offers', element: <AdminOffers /> },
  { path: '/admin/popup', element: <AdminPopup /> },
  { path: '/admin/sitemap', element: <AdminSitemap /> },
];
